import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { 
  MarketData, 
  NewsItem, 
  Alert, 
  EconomicEvent, 
  UserPreferences, 
  WatchlistItem,
  PortfolioItem,
  Notification
} from '../types';
import type { FuturesData, MarketSentimentData } from '../services/marketData';
import type { NewsSentiment } from '../services/newsService';
import type { EconomicIndicator, EconomicInsight } from '../types/economicIndicators';

// Main App Store
interface AppState {
  // UI State
  sidebarOpen: boolean;
  currentPage: string;
  theme: 'dark' | 'light';
  loading: boolean;
  
  // Market Data
  marketData: MarketData[];
  selectedSymbol: string | null;
  futuresData: FuturesData[];
  marketSentiment: MarketSentimentData[];
  
  // Economic Indicators
  economicIndicators: EconomicIndicator[];
  economicInsights: EconomicInsight[];
  economicIndicatorsLoading: boolean;
  
  // News
  news: NewsItem[];
  newsLoading: boolean;
  newsSentiment: NewsSentiment | null;
  
  // Alerts
  alerts: Alert[];
  activeAlerts: Alert[];
  
  // Economic Calendar
  economicEvents: EconomicEvent[];
  
  // Watchlist & Portfolio
  watchlist: WatchlistItem[];
  portfolio: PortfolioItem[];
  
  // Notifications
  notifications: Notification[];
  
  // User Preferences
  preferences: UserPreferences;
  
  // Actions
  setSidebarOpen: (open: boolean) => void;
  setCurrentPage: (page: string) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  setLoading: (loading: boolean) => void;
  
  // Market Actions
  updateMarketData: (data: MarketData[]) => void;
  setSelectedSymbol: (symbol: string | null) => void;
  updateFuturesData: (data: FuturesData[]) => void;
  updateMarketSentiment: (data: MarketSentimentData[]) => void;
  
  // Economic Indicators Actions
  updateEconomicIndicators: (data: EconomicIndicator[]) => void;
  updateEconomicInsights: (data: EconomicInsight[]) => void;
  setEconomicIndicatorsLoading: (loading: boolean) => void;
  
  // News Actions
  setNews: (news: NewsItem[]) => void;
  setNewsLoading: (loading: boolean) => void;
  addNewsItem: (item: NewsItem) => void;
  updateNewsSentiment: (sentiment: NewsSentiment) => void;
  
  // Alert Actions
  addAlert: (alert: Omit<Alert, 'id' | 'createdAt'>) => void;
  removeAlert: (id: string) => void;
  toggleAlert: (id: string) => void;
  triggerAlert: (id: string) => void;
  
  // Economic Calendar Actions
  setEconomicEvents: (events: EconomicEvent[]) => void;
  
  // Watchlist Actions
  addToWatchlist: (item: Omit<WatchlistItem, 'id' | 'addedAt'>) => void;
  removeFromWatchlist: (id: string) => void;
  
  // Portfolio Actions
  addToPortfolio: (item: Omit<PortfolioItem, 'id' | 'addedAt'>) => void;
  removeFromPortfolio: (id: string) => void;
  updatePortfolioItem: (id: string, updates: Partial<PortfolioItem>) => void;
  
  // Notification Actions
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Preferences Actions
  updatePreferences: (preferences: Partial<UserPreferences>) => void;
}

const defaultPreferences: UserPreferences = {
  theme: 'dark',
  currency: 'USD',
  language: 'en',
  notifications: {
    push: true,
    email: false,
    sound: true,
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    }
  },
  dashboard: {
    layout: 'grid',
    refreshInterval: 30000,
    showAnimations: true
  }
};



export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial State
      sidebarOpen: true,
      currentPage: 'dashboard',
      theme: 'dark',
      loading: false,
      
      marketData: [],
      selectedSymbol: null,
      futuresData: [],
      marketSentiment: [],
      
      economicIndicators: [],
      economicInsights: [],
      economicIndicatorsLoading: false,
      
      news: [],
      newsLoading: false,
      newsSentiment: null,
      
      alerts: [],
      activeAlerts: [],
      
      economicEvents: [],
      
      watchlist: [],
      portfolio: [],
      
      notifications: [],
      
      preferences: defaultPreferences,
      
      // UI Actions
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setCurrentPage: (page) => set({ currentPage: page }),
      setTheme: (theme) => set({ theme }),
      setLoading: (loading) => set({ loading }),
      
      // Market Actions
      updateMarketData: (data) => set({ marketData: data }),
      setSelectedSymbol: (symbol) => set({ selectedSymbol: symbol }),
      updateFuturesData: (data) => set({ futuresData: data }),
      updateMarketSentiment: (data) => set({ marketSentiment: data }),
      
      // Economic Indicators Actions
      updateEconomicIndicators: (data) => set({ economicIndicators: data }),
      updateEconomicInsights: (data) => set({ economicInsights: data }),
      setEconomicIndicatorsLoading: (loading) => set({ economicIndicatorsLoading: loading }),
      
      // News Actions
      setNews: (news) => {
        console.log('🏪 Store setNews called with:', news?.length || 0, 'articles');
        console.log('🏪 First news item:', news?.[0]?.title || 'No news');
        set({ news });
        console.log('🏪 News set in store successfully');
      },
      setNewsLoading: (loading) => {
        console.log('🏪 Store setNewsLoading called with:', loading);
        set({ newsLoading: loading });
      },
      addNewsItem: (item) => set((state) => ({ 
        news: [item, ...state.news].slice(0, 100) // Keep only latest 100
      })),
      updateNewsSentiment: (sentiment) => set({ newsSentiment: sentiment }),
      
      // Alert Actions
      addAlert: (alertData) => {
        const alert: Alert = {
          ...alertData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString()
        };
        set((state) => ({ 
          alerts: [...state.alerts, alert],
          activeAlerts: alert.isActive ? [...state.activeAlerts, alert] : state.activeAlerts
        }));
      },
      
      removeAlert: (id) => set((state) => ({
        alerts: state.alerts.filter(a => a.id !== id),
        activeAlerts: state.activeAlerts.filter(a => a.id !== id)
      })),
      
      toggleAlert: (id) => set((state) => {
        const alerts = state.alerts.map(alert => 
          alert.id === id ? { ...alert, isActive: !alert.isActive } : alert
        );
        const activeAlerts = alerts.filter(a => a.isActive);
        return { alerts, activeAlerts };
      }),
      
      triggerAlert: (id) => set((state) => ({
        alerts: state.alerts.map(alert => 
          alert.id === id ? { ...alert, triggeredAt: new Date().toISOString() } : alert
        )
      })),
      
      // Economic Calendar Actions
      setEconomicEvents: (events) => set({ economicEvents: events }),
      
      // Watchlist Actions
      addToWatchlist: (itemData) => {
        const item: WatchlistItem = {
          ...itemData,
          id: crypto.randomUUID(),
          addedAt: new Date().toISOString()
        };
        set((state) => ({ 
          watchlist: [...state.watchlist, item]
        }));
      },
      
      removeFromWatchlist: (id) => set((state) => ({
        watchlist: state.watchlist.filter(item => item.id !== id)
      })),
      
      // Portfolio Actions
      addToPortfolio: (itemData) => {
        const item: PortfolioItem = {
          ...itemData,
          id: crypto.randomUUID(),
          addedAt: new Date().toISOString()
        };
        set((state) => ({ 
          portfolio: [...state.portfolio, item]
        }));
      },
      
      removeFromPortfolio: (id) => set((state) => ({
        portfolio: state.portfolio.filter(item => item.id !== id)
      })),
      
      updatePortfolioItem: (id, updates) => set((state) => ({
        portfolio: state.portfolio.map(item => 
          item.id === id ? { ...item, ...updates } : item
        )
      })),
      
      // Notification Actions
      addNotification: (notificationData) => {
        const notification: Notification = {
          ...notificationData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString()
        };
        set((state) => ({ 
          notifications: [notification, ...state.notifications].slice(0, 50)
        }));
      },
      
      removeNotification: (id) => set((state) => ({
        notifications: state.notifications.filter(n => n.id !== id)
      })),
      
      clearNotifications: () => set({ notifications: [] }),
      
      // Preferences Actions
      updatePreferences: (newPreferences) => set((state) => ({
        preferences: { ...state.preferences, ...newPreferences }
      }))
    }),
    {
      name: 'finalert-storage',
      partialize: (state) => ({
        theme: state.theme,
        preferences: state.preferences,
        watchlist: state.watchlist,
        portfolio: state.portfolio,
        alerts: state.alerts,
        // Include news in persistence to maintain state
        news: state.news,
        newsSentiment: state.newsSentiment
      })
    }
  )
);

// Selectors for better performance
export const useMarketData = () => useAppStore(state => state.marketData);
export const useFuturesData = () => useAppStore(state => state.futuresData);
export const useMarketSentiment = () => useAppStore(state => state.marketSentiment);
export const useEconomicIndicators = () => useAppStore(state => state.economicIndicators);
export const useEconomicInsights = () => useAppStore(state => state.economicInsights);
export const useEconomicIndicatorsLoading = () => useAppStore(state => state.economicIndicatorsLoading);
export const useNews = () => useAppStore(state => state.news);
export const useNewsSentiment = () => useAppStore(state => state.newsSentiment);
export const useAlerts = () => useAppStore(state => state.alerts);
export const useWatchlist = () => useAppStore(state => state.watchlist);
export const usePortfolio = () => useAppStore(state => state.portfolio);
export const useNotifications = () => useAppStore(state => state.notifications);
export const usePreferences = () => useAppStore(state => state.preferences);
export const useTheme = () => useAppStore(state => state.theme);
export const useLoading = () => useAppStore(state => state.loading);
export const useSidebarOpen = () => useAppStore(state => state.sidebarOpen);
export const useCurrentPage = () => useAppStore(state => state.currentPage);